import {
  CollectionBuildHistoryRecord,
  CollectionBuildLookPayload,
  CollectionBuildSessionDetail,
  CollectionBuildSessionPayload,
  createCollectionBuildSession,
  updateCollectionBuildSession,
} from "../../app/api/collectionBuild";
import {
  CollectionBuildLook,
  CollectionBuildMoodboardVersion,
  CollectionBuildResultDetails,
  ColorPaletteOption,
  PatternTrendChoice,
  PreferenceOption,
  SilhouetteGroup,
  StartingDirection,
  normalizeGarmentFormats,
  patternTrendLabel,
  snapshotCollectionBuildDetails,
  trimmedPantoneField,
  withSyncedGarmentFormatFields,
  normalizeCollectionBuildInfluence,
} from "./collectionBuildConstants";
import {
  denimTilesFromStyleSelections,
  humanizeCollectionThemeId,
  resolveDenimCatalogTile,
  resolveDenimWashDisplay,
} from "./collectionBuildCatalog";

export {
  collectionSheetsFromOutputs,
  collectionBuildSheetSlotLabel as collectionSheetSlotLabel,
} from "./collectionBuildDesignSheetConstants";

/**
 * Distinct from Image Generator's `session` query param (which is an IG *output* id).
 * Collection Build must use generation_sessions.id via collection-build APIs only.
 */
export const COLLECTION_BUILD_SESSION_PARAM = "cbSession";
/** Older CB links used `?session=` — migrate away; do not treat as trusted CB id. */
export const COLLECTION_BUILD_SESSION_PARAM_LEGACY = "session";

/** localStorage key prefixes (suffix `:userId`) — Collection Build sessions polling. */
export const COLLECTION_BUILD_PENDING_STORAGE_PREFIX =
  "ich-cb-pending-sessions";
export const COLLECTION_BUILD_NOTIFICATION_STORAGE_PREFIX =
  "ich-cb-notifications";
export const COLLECTION_BUILD_NOTIFIED_STORAGE_PREFIX = "ich-cb-notified-keys";
export const COLLECTION_BUILD_SESSION_POLL_MS = 4000;
export const COLLECTION_BUILD_NOTIFIED_KEYS_LIMIT = 200;
export const COLLECTION_BUILD_NOTIFICATIONS_LIST_LIMIT = 40;

/** IG pending keys Collection Build must never write; may clear mistaken ids on CB mount. */
const IG_PENDING_STORAGE_PREFIX = "ich-ig-pending-ids";
const IG_PENDING_SHEET_STORAGE_PREFIX = "ich-ig-pending-sheets";

/** Backend `generation_sessions.id` is a positive integer (Joi min 1). */
export const parseCollectionBuildSessionId = (
  value: unknown
): number | undefined => {
  if (typeof value === "number") {
    return Number.isInteger(value) && value >= 1 ? value : undefined;
  }
  if (typeof value === "string" && value.trim()) {
    const id = Number(value);
    return Number.isInteger(id) && id >= 1 ? id : undefined;
  }
  return undefined;
};

/** Active CB session id — only `cbSession` (never IG's `session`). */
export const readCollectionBuildSessionParam = (
  searchParams: URLSearchParams
): number | undefined =>
  parseCollectionBuildSessionId(
    searchParams.get(COLLECTION_BUILD_SESSION_PARAM)
  );

export const readLegacyCollectionBuildSessionParam = (
  searchParams: URLSearchParams
): number | undefined =>
  parseCollectionBuildSessionId(
    searchParams.get(COLLECTION_BUILD_SESSION_PARAM_LEGACY)
  );

export const collectionBuildSessionSearchParams = (
  sessionId: number,
  current?: URLSearchParams
): URLSearchParams => {
  const next = new URLSearchParams(current);
  next.delete(COLLECTION_BUILD_SESSION_PARAM_LEGACY);
  next.set(COLLECTION_BUILD_SESSION_PARAM, String(sessionId));
  return next;
};

/**
 * Remove a mistaken id from IG pending localStorage (CB path only).
 * Does not write IG keys — only deletes an id that may have been stored while CB
 * reused `?session=`.
 */
export const clearMistakenImageGeneratorPendingId = (
  userId: string,
  id: number
) => {
  if (!userId || !parseCollectionBuildSessionId(id)) {
    return;
  }
  for (const prefix of [
    IG_PENDING_STORAGE_PREFIX,
    IG_PENDING_SHEET_STORAGE_PREFIX,
  ]) {
    const key = `${prefix}:${userId}`;
    try {
      const raw = localStorage.getItem(key);
      const parsed = raw ? (JSON.parse(raw) as number[]) : [];
      if (!Array.isArray(parsed) || !parsed.includes(id)) {
        continue;
      }
      localStorage.setItem(
        key,
        JSON.stringify(parsed.filter(item => item !== id))
      );
    } catch {
      // Ignore malformed storage.
    }
  }
};

/**
 * Drop empty strings / unused direction fields so Joi and logs stay clean.
 * Keeps intentional false/0 and non-empty arrays/objects.
 */
export const sanitizeCollectionBuildSessionPayload = (
  payload: CollectionBuildSessionPayload
): CollectionBuildSessionPayload => {
  const direction = String(payload.startingDirection || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");
  const isDenim = direction === "denim";
  const next: Record<string, unknown> = {
    season: payload.season,
    genre: payload.genre,
    market: payload.market,
    category: payload.category,
    rangePlan: {
      items: (payload.rangePlan?.items || [])
        .map(item => ({
          productType: String(item?.productType || "").trim(),
          quantity: Number(item?.quantity || 0),
        }))
        .filter(item => Boolean(item.productType) && item.quantity > 0),
    },
  };

  const assignIfPresent = (key: string, value: unknown) => {
    if (value === undefined || value === null) {
      return;
    }
    if (typeof value === "string" && !value.trim()) {
      return;
    }
    if (Array.isArray(value) && value.length === 0) {
      return;
    }
    next[key] = value;
  };

  assignIfPresent("seasonId", payload.seasonId);
  if (direction) {
    next.startingDirection = direction;
  }
  if (payload.skipReportThemes === true || isDenim) {
    next.skipReportThemes = true;
  } else if (payload.skipReportThemes === false) {
    next.skipReportThemes = false;
  }
  if (payload.skipMoodboard === true) {
    next.skipMoodboard = true;
  } else if (payload.skipMoodboard === false) {
    next.skipMoodboard = false;
  }

  // Direction-specific: never send the other path's empty junk.
  if (!isDenim) {
    const seenSurfaceIds = new Set<string>();
    const surfaceIds: string[] = [];
    const pushSurfaceId = (value: unknown) => {
      const trimmed = String(value || "").trim();
      if (!trimmed || seenSurfaceIds.has(trimmed)) {
        return;
      }
      seenSurfaceIds.add(trimmed);
      surfaceIds.push(trimmed);
    };
    if (Array.isArray(payload.surfaceIds) && payload.surfaceIds.length) {
      payload.surfaceIds.forEach(pushSurfaceId);
    } else {
      pushSurfaceId(payload.surfaceId);
    }
    const surfaceId = surfaceIds[0] || "";
    assignIfPresent("macroThemeId", payload.macroThemeId);
    if (surfaceIds.length) {
      assignIfPresent("surfaceId", surfaceId);
      assignIfPresent("surfaceIds", surfaceIds);
      // Surface trend is mutually exclusive with micro-theme tiles.
      next.patternChoice = "none";
    } else {
      assignIfPresent("patternTrendId", payload.patternTrendId);
      assignIfPresent("patternTrendIds", payload.patternTrendIds);
      assignIfPresent("patternChoice", payload.patternChoice);
      assignIfPresent("selectedAssetStems", payload.selectedAssetStems);
    }
    assignIfPresent("garmentFormat", payload.garmentFormat);
    assignIfPresent("embroideryType", payload.embroideryType);
    assignIfPresent("patternFormat", payload.patternFormat);
  } else {
    assignIfPresent("denimMaterialId", payload.denimMaterialId);
    assignIfPresent("denimWashId", payload.denimWashId);
    next.patternChoice = "none";
  }

  assignIfPresent("colourStoryId", payload.colourStoryId);
  assignIfPresent("colourStoryIds", payload.colourStoryIds);
  assignIfPresent("paletteHexes", payload.paletteHexes);
  assignIfPresent("additionalPrompt", payload.additionalPrompt?.trim());
  assignIfPresent("themeLabel", payload.themeLabel);
  assignIfPresent("sessionTitle", payload.sessionTitle);

  if (Array.isArray(payload.looks) && payload.looks.length) {
    next.looks = payload.looks.map(look =>
      sanitizeCollectionBuildLookPayload(look)
    );
  }

  if (Array.isArray(payload.palettes) && payload.palettes.length) {
    next.palettes = payload.palettes.map(palette => {
      const entry: Record<string, unknown> = {
        id: palette.id,
        colors: palette.colors || [],
      };
      if (palette.label?.trim()) {
        entry.label = palette.label;
      }
      if (palette.swatches?.length) {
        entry.swatches = palette.swatches
          .filter(swatch => swatch?.hex?.trim())
          .map(swatch => {
            const row: Record<string, unknown> = { hex: swatch.hex.trim() };
            if (swatch.name?.trim()) {
              row.name = swatch.name.trim();
            }
            if (swatch.tier === "major" || swatch.tier === "minor") {
              row.tier = swatch.tier;
            }
            Object.assign(row, trimmedPantoneField(swatch.pantone));
            return row;
          });
      }
      return entry;
    });
  }

  return next as CollectionBuildSessionPayload;
};

const sanitizeCollectionBuildLookPayload = (
  look: CollectionBuildLookPayload
): CollectionBuildLookPayload => {
  const next: Record<string, unknown> = {
    productType: look.productType,
  };
  const assignIfPresent = (key: string, value: unknown) => {
    if (value === undefined || value === null) {
      return;
    }
    if (typeof value === "string" && !value.trim()) {
      return;
    }
    if (Array.isArray(value) && value.length === 0) {
      return;
    }
    next[key] = value;
  };
  assignIfPresent("outputNumber", look.outputNumber);
  assignIfPresent("styleIndex", look.styleIndex);
  assignIfPresent(
    "influence",
    normalizeCollectionBuildInfluence(look.influence)
  );
  assignIfPresent("styleType", look.styleType);
  assignIfPresent("material", look.material);
  assignIfPresent("materials", look.materials);
  assignIfPresent("sleeve", look.sleeve);
  assignIfPresent("neckline", look.neckline);
  assignIfPresent("fit", look.fit);
  assignIfPresent("wash", look.wash);
  assignIfPresent("length", look.length);
  assignIfPresent(
    "denimMaterialId",
    look.denimMaterialId ||
      (Array.isArray(look.denimMaterialIds)
        ? look.denimMaterialIds.map(id => String(id || "").trim()).find(Boolean)
        : undefined)
  );
  assignIfPresent(
    "denimWashId",
    look.denimWashId ||
      (Array.isArray(look.denimWashIds)
        ? look.denimWashIds.map(id => String(id || "").trim()).find(Boolean)
        : undefined)
  );
  const garmentFormats = normalizeGarmentFormats(
    (look.garmentFormats || []).map(entry => ({
      format: String(entry?.format || "").trim(),
      rank: entry?.rank,
      patternFormat: String(entry?.patternFormat || "").trim(),
      embroideryType: String(entry?.embroideryType || "").trim(),
    })),
    {
      garmentFormat: look.garmentFormat,
      patternFormat: look.patternFormat,
      embroideryType: look.embroideryType,
    }
  );
  if (garmentFormats.length) {
    next.garmentFormats = garmentFormats.map(entry => {
      const row: Record<string, unknown> = {
        format: entry.format,
        rank: entry.rank,
      };
      if (entry.patternFormat) {
        row.patternFormat = entry.patternFormat;
      }
      if (entry.format === "Embroidery" && entry.embroideryType) {
        row.embroideryType = entry.embroideryType;
      }
      return row;
    });
    const first = garmentFormats[0];
    assignIfPresent("garmentFormat", first.format);
    assignIfPresent("patternFormat", first.patternFormat);
    assignIfPresent(
      "embroideryType",
      first.format === "Embroidery" ? first.embroideryType : undefined
    );
  } else {
    assignIfPresent("garmentFormat", look.garmentFormat);
    assignIfPresent("patternFormat", look.patternFormat);
    assignIfPresent(
      "embroideryType",
      look.garmentFormat === "Embroidery" ? look.embroideryType : undefined
    );
  }
  assignIfPresent("silhouetteId", look.silhouetteId);
  assignIfPresent("silhouetteName", look.silhouetteName);
  assignIfPresent("selectedSketchStems", look.selectedSketchStems);
  assignIfPresent("sketchUploadName", look.sketchUploadName);
  assignIfPresent("sketchUploadUrl", look.sketchUploadUrl);
  assignIfPresent("additionalPrompt", look.additionalPrompt?.trim());
  assignIfPresent("frontUrl", look.frontUrl);
  assignIfPresent("backUrl", look.backUrl);
  return next as CollectionBuildLookPayload;
};

/** Deduplicate concurrent POSTs when the UI fires generate twice before URL updates. */
let inFlightSessionCreate: Promise<number | null> | null = null;
let inFlightSessionUpdate: Promise<number | null> | null = null;
/** Survives the create promise clearing so a second caller before URL updates does not POST again. */
let draftSessionId: number | null = null;
/** Bumped on clear so in-flight creates do not resurrect a discarded draft. */
let draftEpoch = 0;

/** Forget the in-memory draft id (fresh nav without cbSession, generate failure). */
export const clearCollectionBuildDraftSessionId = () => {
  draftSessionId = null;
  draftEpoch += 1;
};

/** Align module draft cache with `cbSession` from the URL. */
export const rememberCollectionBuildDraftSessionId = (sessionId: number) => {
  const parsed = parseCollectionBuildSessionId(sessionId);
  if (parsed) {
    draftSessionId = parsed;
  }
};

/** In-memory draft/session id bound after load or create (survives brief URL gaps). */
export const peekCollectionBuildDraftSessionId = (): number | null =>
  parseCollectionBuildSessionId(draftSessionId) ?? null;

/**
 * Create once (POST /session) or update (PATCH /session/:id) with sanitized selections.
 * Never POSTs when `existingSessionId` or the in-memory draft id is already set.
 * Returns null when the draft was cleared mid-flight (caller must not bind URL).
 */
export const persistCollectionBuildSession = async (
  payload: CollectionBuildSessionPayload,
  existingSessionId?: number | null
): Promise<number | null> => {
  const sanitized = sanitizeCollectionBuildSessionPayload(payload);
  const epochAtStart = draftEpoch;
  const existing =
    parseCollectionBuildSessionId(existingSessionId) ||
    parseCollectionBuildSessionId(draftSessionId);

  if (existing) {
    if (inFlightSessionUpdate) {
      await inFlightSessionUpdate.catch(() => undefined);
    }
    if (epochAtStart !== draftEpoch) {
      return null;
    }
    inFlightSessionUpdate = (async () => {
      try {
        const updated = await updateCollectionBuildSession(existing, sanitized);
        const id =
          parseCollectionBuildSessionId(updated?.sessionId) || existing;
        if (epochAtStart === draftEpoch) {
          draftSessionId = id;
          return id;
        }
        return null;
      } finally {
        inFlightSessionUpdate = null;
      }
    })();
    return inFlightSessionUpdate;
  }

  // Join an in-flight create, then PATCH with the caller's latest payload.
  if (inFlightSessionCreate) {
    const createdId = await inFlightSessionCreate;
    if (epochAtStart !== draftEpoch) {
      // Fresh-nav / clear happened while we waited — do not POST another session.
      return null;
    }
    if (parseCollectionBuildSessionId(draftSessionId) === createdId) {
      return persistCollectionBuildSession(sanitized, createdId);
    }
    // Prior create was abandoned (epoch bump); start one fresh create for this epoch.
    return persistCollectionBuildSession(sanitized, null);
  }

  const epochAtCreate = draftEpoch;
  inFlightSessionCreate = (async () => {
    try {
      const created = await createCollectionBuildSession(sanitized);
      const sessionId = parseCollectionBuildSessionId(created?.sessionId);
      if (!sessionId) {
        throw new Error("Collection session was not created.");
      }
      if (epochAtCreate !== draftEpoch) {
        // Discarded draft — do not publish id to callers / waiters as bindable.
        return null;
      }
      draftSessionId = sessionId;
      return sessionId;
    } finally {
      inFlightSessionCreate = null;
    }
  })();
  return inFlightSessionCreate;
};

/**
 * Reuse an existing positive DB session id, or POST /collection-build/session first.
 * Always upserts current selections when an id already exists.
 */
export const ensureCollectionBuildSessionId = async (
  payload: CollectionBuildSessionPayload,
  existingSessionId?: number | null
): Promise<number> => {
  const sessionId = await persistCollectionBuildSession(
    payload,
    existingSessionId
  );
  if (!sessionId) {
    throw new Error("Collection session was not created.");
  }
  return sessionId;
};

export type CollectionBuildGenerationKind = "moodboard" | "look";

const outputIsPending = (item: CollectionBuildHistoryRecord) =>
  item.status === "generating" || item.status === "assembled";

const outputHasImage = (item: CollectionBuildHistoryRecord) =>
  Boolean(item.moodboardUrl || item.imageUrl || item.backImageUrl);

const isSuccessfulCollectionBuildOutput = (
  item: CollectionBuildHistoryRecord | null | undefined
) => Boolean(item && (item.status === "generated" || outputHasImage(item)));

/** In-flight moodboard/look rows, or a design sheet still generating. Used to keep polling. */
export const collectionSessionIsBusy = (
  session: CollectionBuildSessionDetail | null | undefined
) =>
  Boolean(
    session?.outputs.some(
      item =>
        outputIsPending(item) ||
        item.details?.designSheet?.status === "generating"
    )
  );

/** Newest output by createdAt, then id. Retry rows are later than the failed attempt. */
export const newestCollectionBuildOutput = (
  outputs: CollectionBuildHistoryRecord[] | null | undefined
): CollectionBuildHistoryRecord | null => {
  const items = outputs || [];
  if (!items.length) {
    return null;
  }
  return items.reduce((latest, item) => {
    const latestTs = new Date(latest.createdAt).getTime();
    const itemTs = new Date(item.createdAt).getTime();
    if (itemTs > latestTs || (itemTs === latestTs && item.id > latest.id)) {
      return item;
    }
    return latest;
  });
};

const collectionBuildRecentsStage = (
  skipMoodboard?: boolean
): CollectionBuildGenerationKind => (skipMoodboard ? "look" : "moodboard");

/**
 * Output Recents should describe: latest generated moodboard (looks if
 * skip-moodboard), not a later failed look / regen / sheet sibling.
 */
export const preferredCollectionBuildRecentsOutput = (
  outputs: CollectionBuildHistoryRecord[] | null | undefined,
  skipMoodboard?: boolean
): CollectionBuildHistoryRecord | null => {
  const items = outputs || [];
  if (!items.length) {
    return null;
  }
  const stageItems = items.filter(
    item => item.outputType === collectionBuildRecentsStage(skipMoodboard)
  );
  const successful = (rows: CollectionBuildHistoryRecord[]) =>
    rows.filter(item => isSuccessfulCollectionBuildOutput(item));
  return (
    newestCollectionBuildOutput(successful(stageItems)) ||
    newestCollectionBuildOutput(stageItems) ||
    newestCollectionBuildOutput(successful(items)) ||
    newestCollectionBuildOutput(items)
  );
};

/** Recents helper, but never a failed sibling without an image. */
export const preferredCollectionBuildGeneratedOutput = (
  outputs: CollectionBuildHistoryRecord[] | null | undefined,
  skipMoodboard?: boolean
): CollectionBuildHistoryRecord | null => {
  const preferred = preferredCollectionBuildRecentsOutput(
    outputs,
    skipMoodboard
  );
  return isSuccessfulCollectionBuildOutput(preferred) ? preferred : null;
};

const preferredGeneratedLooks = (
  outputs: CollectionBuildHistoryRecord[] | null | undefined
): CollectionBuildHistoryRecord[] => {
  const successful = (outputs || []).filter(
    item =>
      item.outputType === "look" && isSuccessfulCollectionBuildOutput(item)
  );
  const groups = new Map<number, CollectionBuildHistoryRecord[]>();
  successful.forEach(item => {
    const key = item.outputNumber || item.id;
    const list = groups.get(key) || [];
    list.push(item);
    groups.set(key, list);
  });
  return Array.from(groups.values())
    .map(group => newestCollectionBuildOutput(group))
    .filter((item): item is CollectionBuildHistoryRecord => Boolean(item));
};

/** POST /approve ids for each look's preferred generated version. */
export const collectionBuildLookApproveIds = (
  outputs: CollectionBuildHistoryRecord[] | null | undefined
): number[] => preferredGeneratedLooks(outputs).map(item => item.id);

/**
 * Recents status from the current stage, not the latest row of any type.
 * Prefer a successful generated moodboard (or looks if skip-moodboard) over
 * older/newer failed siblings. Failed only when that stage has no success
 * and the latest attempt failed. Retry: Failed → generating → generated.
 */
export const collectionSessionDisplayStatus = (
  session: CollectionBuildSessionDetail | null | undefined
): string => {
  if (!session) {
    return "draft";
  }
  const outputs = session.outputs || [];
  if (outputs.some(outputIsPending)) {
    return "generating";
  }
  const stage = collectionBuildRecentsStage(session.skipMoodboard);
  const stageOutputs = outputs.filter(item => item.outputType === stage);
  if (
    stageOutputs.some(
      item => item.status === "generated" || outputHasImage(item)
    )
  ) {
    return "generated";
  }
  const latestStage = newestCollectionBuildOutput(stageOutputs);
  if (latestStage?.status === "failed") {
    return "failed";
  }
  if (
    outputs.some(item => item.status === "generated" || outputHasImage(item))
  ) {
    return "generated";
  }
  const latest = newestCollectionBuildOutput(outputs);
  if (latest?.status === "failed") {
    return "failed";
  }
  return session.status || "draft";
};

export const collectionMoodboardApproved = (
  session: CollectionBuildSessionDetail | null | undefined
) => {
  if (!session || session.skipMoodboard) {
    return false;
  }
  const preferred = preferredCollectionBuildGeneratedOutput(
    session.outputs,
    false
  );
  return Boolean(
    preferred?.outputType === "moodboard" && preferred.review?.approvedAt
  );
};

export const collectionLooksApproved = (
  session: CollectionBuildSessionDetail | null | undefined
) => {
  const preferredLooks = preferredGeneratedLooks(session?.outputs);
  return (
    preferredLooks.length > 0 &&
    preferredLooks.every(item => Boolean(item.review?.approvedAt))
  );
};

/** Draft sessions (inputs only) must not open the Results view. */
export const collectionSessionHasOutputs = (
  session: CollectionBuildSessionDetail | null | undefined
) => Boolean(session?.outputs?.length);

export type CollectionBuildGenerationErrorCopy = {
  title: string;
  reason: string;
  toast: string;
};

export const collectionBuildOutputsPending = (
  outputs: CollectionBuildHistoryRecord[] | null | undefined,
  outputType: CollectionBuildGenerationKind
) =>
  Boolean(
    outputs?.some(
      item => item.outputType === outputType && outputIsPending(item)
    )
  );

export const collectionBuildFailedOutput = (
  outputs: CollectionBuildHistoryRecord[] | null | undefined,
  outputType: CollectionBuildGenerationKind
): CollectionBuildHistoryRecord | null => {
  const items = (outputs || []).filter(item => item.outputType === outputType);
  if (
    !items.length ||
    items.some(outputIsPending) ||
    items.some(outputHasImage)
  ) {
    return null;
  }
  const latest = newestCollectionBuildOutput(items);
  return latest?.status === "failed" ? latest : null;
};

/** User-facing copy for CB moodboard vs look failures. Rewrites IG-style timeout text. */
export const collectionBuildGenerationErrorCopy = (
  outputType: CollectionBuildGenerationKind,
  errorMessage?: string | null
): CollectionBuildGenerationErrorCopy => {
  const noun = outputType === "moodboard" ? "Moodboard" : "Look";
  const raw = String(errorMessage || "").trim();
  const isTimeout = /timed out|timeout/i.test(raw);
  if (isTimeout) {
    return {
      title: `${noun} generation timed out`,
      reason:
        outputType === "moodboard"
          ? "The moodboard took too long to finish. Retry with the same selections, or go back to edit them."
          : "Look generation took too long to finish. Retry with the same selections, or go back to edit them.",
      toast: `${noun} generation timed out.`,
    };
  }
  const usableServerMessage = raw && !/start a new look/i.test(raw) ? raw : "";
  if (usableServerMessage) {
    return {
      title: `${noun} generation failed`,
      reason: usableServerMessage,
      toast: `${noun} generation failed.`,
    };
  }
  return {
    title: `${noun} generation failed`,
    reason:
      outputType === "moodboard"
        ? "The moodboard didn’t finish. Retry with the same selections, or go back to edit them."
        : "Look generation didn’t finish. Retry with the same selections, or go back to edit them.",
    toast: `${noun} generation failed.`,
  };
};

export const looksPayloadFromSessionOutputs = (
  outputs: CollectionBuildHistoryRecord[] | null | undefined
): CollectionBuildLookPayload[] => {
  const looks = (outputs || []).filter(item => item.outputType === "look");
  const byNumber = new Map<number, CollectionBuildHistoryRecord>();
  looks.forEach(item => {
    const key = item.outputNumber || item.id;
    const existing = byNumber.get(key);
    if (
      !existing ||
      new Date(item.createdAt).getTime() >
        new Date(existing.createdAt).getTime()
    ) {
      byNumber.set(key, item);
    }
  });
  return Array.from(byNumber.entries())
    .sort(([left], [right]) => left - right)
    .map(([, item]) => {
      const payload = (item.requestPayload || {}) as {
        look?: CollectionBuildLookPayload;
      };
      const look = payload.look;
      return sanitizeCollectionBuildLookPayload({
        outputNumber: item.outputNumber || undefined,
        styleIndex: look?.styleIndex,
        productType: look?.productType || item.details?.productType || "Look",
        influence: look?.influence || item.details?.influence,
        styleType: look?.styleType || item.details?.styleType,
        material: look?.material || item.details?.material,
        materials: look?.materials || look?.material || item.details?.material,
        sleeve: look?.sleeve || item.details?.sleeve,
        neckline: look?.neckline || item.details?.neckline,
        fit: look?.fit || item.details?.fit,
        wash: look?.wash || item.details?.wash,
        length: look?.length || item.details?.length,
        denimMaterialId:
          look?.denimMaterialId ||
          look?.denimMaterialIds
            ?.map(id => String(id || "").trim())
            .find(Boolean),
        denimWashId:
          look?.denimWashId ||
          look?.denimWashIds?.map(id => String(id || "").trim()).find(Boolean),
        garmentFormats: look?.garmentFormats,
        garmentFormat:
          look?.garmentFormat || item.details?.garmentFormat || undefined,
        patternFormat:
          look?.patternFormat || item.details?.patternFormat || undefined,
        embroideryType: look?.embroideryType,
        silhouetteId: look?.silhouetteId,
        silhouetteName: look?.silhouetteName || item.details?.silhouetteName,
        selectedSketchStems: look?.selectedSketchStems,
        sketchUploadName: look?.sketchUploadName,
        sketchUploadUrl: look?.sketchUploadUrl,
        additionalPrompt: look?.additionalPrompt,
      });
    });
};

export type CollectionBuildTitleSource = {
  title?: string | null;
  market?: string | null;
  category?: string | null;
  season?: string | null;
  /** Macro / capsule / denim-wash display name. */
  theme?: string | null;
  lookCount?: number | null;
  moodboardCount?: number | null;
  status?: string | null;
};

const isGenericCollectionTitle = (title: string) => {
  const normalized = title.trim().toLowerCase();
  if (
    !normalized ||
    normalized === "collection" ||
    normalized === "draft collection" ||
    normalized === "collection · collection" ||
    normalized === "collection_build"
  ) {
    return true;
  }
  // Legacy BE create title: season · startingDirection · collection
  return /(?:^| · )(macro-theme|macrotheme|denim|capsules|capsule) · collection$/.test(
    normalized
  );
};

const pushUniqueTitlePart = (parts: string[], part: string) => {
  const trimmed = part.trim();
  if (!trimmed) {
    return;
  }
  if (
    parts.some(existing => existing.toLowerCase() === trimmed.toLowerCase())
  ) {
    return;
  }
  parts.push(trimmed);
};

/**
 * Recents / tracker label for a CB session.
 * Prefer Market · Theme/wash when a theme is known; else a persisted
 * non-generic API title; else Market · Category / Season.
 */
export const collectionBuildSummaryTitle = (
  source: CollectionBuildTitleSource
): string => {
  const market = String(source.market || "").trim();
  const category = String(source.category || "").trim();
  const season = String(source.season || "").trim();
  const theme = String(source.theme || "").trim();
  const parts: string[] = [];

  if (theme) {
    // Distinctive: Market · Theme (or Category · Theme when market missing).
    pushUniqueTitlePart(parts, market);
    if (!market) {
      pushUniqueTitlePart(parts, category);
    }
    pushUniqueTitlePart(parts, theme);
    return parts.join(" · ");
  }

  const raw = String(source.title || "").trim();
  if (!isGenericCollectionTitle(raw)) {
    return raw;
  }

  pushUniqueTitlePart(parts, market);
  pushUniqueTitlePart(parts, category || season);
  if (parts.length) {
    return parts.join(" · ");
  }

  const hasOutputs =
    Number(source.lookCount || 0) > 0 || Number(source.moodboardCount || 0) > 0;
  const status = String(source.status || "").toLowerCase();
  const looksGenerated =
    hasOutputs ||
    status === "generating" ||
    status === "generated" ||
    status === "failed" ||
    status === "assembled";
  return looksGenerated ? "Collection" : "Draft collection";
};

/**
 * Distinctive Recents name from form/session fields: catalog theme, denim wash,
 * or humanized id. Skips generic direction labels like "Macro-theme".
 */
export const collectionBuildThemeLabelFromFields = (fields: {
  startingDirection?: string | null;
  themeLabel?: string | null;
  macroThemeId?: string | null;
  macroThemeName?: string | null;
  denimWashId?: string | null;
  denimWashName?: string | null;
}): string => {
  const direction = startingDirectionLabelFromValue(fields.startingDirection);
  const stored = String(fields.themeLabel || "").trim();
  const usable = (value: string) =>
    Boolean(value) && !value.includes("|") && !/^\d+$/.test(value);

  if (direction === "Denim") {
    return (
      (usable(stored) ? stored : "") ||
      String(fields.denimWashName || "").trim() ||
      resolveDenimWashDisplay(String(fields.denimWashId || "").trim()) ||
      "Denim"
    );
  }

  if (usable(stored)) {
    return stored;
  }
  const named = String(fields.macroThemeName || "").trim();
  if (usable(named)) {
    return named;
  }
  const rawId = String(fields.macroThemeId || named || "").trim();
  if (!rawId) {
    return "";
  }
  return humanizeCollectionThemeId(rawId) || "";
};

/**
 * Human-readable theme for Recents: macro/capsule name, denim wash, or humanized id.
 */
export const collectionThemeLabelFromSession = (
  session: CollectionBuildSessionDetail
): string | null => {
  const input = (session.inputSelections ||
    {}) as Partial<CollectionBuildSessionPayload> & { macroTheme?: string };
  const latest = preferredCollectionBuildRecentsOutput(
    session.outputs,
    session.skipMoodboard
  );
  const label = collectionBuildThemeLabelFromFields({
    startingDirection:
      input.startingDirection || latest?.details?.startingDirection,
    themeLabel: input.themeLabel,
    macroThemeId: input.macroThemeId || input.macroTheme,
    macroThemeName:
      latest?.details?.macroTheme || latest?.references?.macroTheme,
    denimWashId: input.denimWashId || latest?.details?.denimWashId,
    denimWashName: latest?.details?.wash,
  }).trim();
  return label || null;
};

/** Title + market/category/season/theme from a full session (inputs preferred over outputs). */
export const collectionBuildTitleFromSession = (
  session: CollectionBuildSessionDetail
): {
  title: string;
  market: string | null;
  category: string | null;
  season: string | null;
  theme: string | null;
} => {
  const input = (session.inputSelections ||
    {}) as Partial<CollectionBuildSessionPayload>;
  const latest = preferredCollectionBuildRecentsOutput(
    session.outputs,
    session.skipMoodboard
  );
  const market =
    String(input.market || latest?.details?.market || "").trim() || null;
  const category =
    String(input.category || latest?.details?.category || "").trim() || null;
  const season =
    String(
      input.season || latest?.details?.season || latest?.season || ""
    ).trim() || null;
  const theme = collectionThemeLabelFromSession(session);
  const looks = session.outputs.filter(item => item.outputType === "look");
  const moodboards = session.outputs.filter(
    item => item.outputType === "moodboard"
  );
  return {
    title: collectionBuildSummaryTitle({
      market,
      category,
      season,
      theme,
      lookCount: looks.length,
      moodboardCount: moodboards.length,
      status: collectionSessionDisplayStatus(session),
    }),
    market,
    category,
    season,
    theme,
  };
};

export const looksPayloadFromGroups = (
  groups: SilhouetteGroup[]
): CollectionBuildLookPayload[] => {
  const looks: CollectionBuildLookPayload[] = [];
  groups.forEach(group => {
    group.styles.forEach((style, styleIndex) => {
      const synced = withSyncedGarmentFormatFields(style);
      looks.push(
        sanitizeCollectionBuildLookPayload({
          outputNumber: looks.length + 1,
          styleIndex,
          productType: group.productType,
          influence: normalizeCollectionBuildInfluence(synced.influence),
          styleType: synced.styleType,
          material: synced.materials,
          materials: synced.materials,
          sleeve: synced.sleeve,
          neckline: synced.neckline,
          fit: synced.fit,
          wash: synced.wash,
          length: synced.length,
          denimMaterialId: synced.denimMaterialId || undefined,
          denimWashId: synced.denimWashId || undefined,
          garmentFormats: synced.garmentFormats,
          garmentFormat: synced.garmentFormat || undefined,
          patternFormat: synced.patternFormat || undefined,
          embroideryType:
            synced.garmentFormat === "Embroidery"
              ? synced.embroideryType || undefined
              : undefined,
          silhouetteId:
            synced.silhouetteId || synced.selectedSketchIds[0] || undefined,
          // Real sketch/catalog label only — do not fall back to styleType/productType.
          silhouetteName: synced.silhouetteName || undefined,
          selectedSketchStems: synced.selectedSketchIds,
          sketchUploadName: synced.uploadFileName,
          sketchUploadUrl: synced.uploadUrl || undefined,
          additionalPrompt: synced.additionalPrompt || undefined,
        })
      );
    });
  });
  return looks;
};

/** Positive generation/output id (BIGINT may arrive as string over the wire). */
export const toCollectionBuildOutputId = (value: unknown): number | null => {
  const id = typeof value === "number" ? value : Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
};

/**
 * Design-sheet / approve gate: first version with review.approvedAt and a
 * usable output id (outputId, else version.id). Coerces string/BIGINT ids.
 */
export const collectionBuildApprovedLookOutputId = (
  look: Pick<CollectionBuildLook, "versions"> | null | undefined
): number | null => {
  if (!look?.versions?.length) {
    return null;
  }
  const approved = look.versions.find(version => Boolean(version.approvedAt));
  if (!approved) {
    return null;
  }
  return (
    toCollectionBuildOutputId(approved.outputId) ??
    toCollectionBuildOutputId(approved.id)
  );
};

/**
 * Resolve an approved look's generation id for design-sheet start.
 * Looks at live look versions, then session outputs for the same look
 * (by look id, outputNumber, or styleIndex+productType).
 */
export const resolveCollectionBuildApprovedSheetOutputId = (
  look: CollectionBuildLook | null | undefined,
  outputs?: CollectionBuildHistoryRecord[] | null
): number | null => {
  const fromLook = collectionBuildApprovedLookOutputId(look);
  if (fromLook != null) {
    return fromLook;
  }
  if (!look || !outputs?.length) {
    return null;
  }

  const lookRows = outputs.filter(item => item.outputType === "look");
  const matched = lookRows.filter(item => {
    const groupKey = item.outputNumber || item.id;
    if (`look-${groupKey}` === look.id) {
      return true;
    }
    if (item.outputNumber != null && item.outputNumber === look.number) {
      return true;
    }
    const payload = (item.requestPayload || {}) as {
      look?: { styleIndex?: number; productType?: string };
      styleIndex?: number;
    };
    const styleIndex = payload.look?.styleIndex ?? payload.styleIndex;
    const productType =
      item.details?.productType || payload.look?.productType || "Look";
    return (
      styleIndex === look.styleIndex &&
      String(productType) === String(look.productType)
    );
  });

  const approvedRow = matched
    .filter(item => Boolean(item.review?.approvedAt))
    .sort(
      (left, right) =>
        new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
    )[0];
  return approvedRow ? toCollectionBuildOutputId(approvedRow.id) : null;
};

/** Keep optimistic lookbook approvals when a session poll omits review.approvedAt. */
export const mergeCollectionLookApprovals = (
  previous: CollectionBuildLook[],
  incoming: CollectionBuildLook[]
): CollectionBuildLook[] => {
  const approvedAtByOutputId = new Map<number, string>();
  previous.forEach(look => {
    look.versions.forEach(version => {
      const outputId =
        toCollectionBuildOutputId(version.outputId) ??
        toCollectionBuildOutputId(version.id);
      if (outputId && version.approvedAt) {
        approvedAtByOutputId.set(outputId, version.approvedAt);
      }
    });
  });
  if (!approvedAtByOutputId.size) {
    return incoming;
  }
  return incoming.map(look => ({
    ...look,
    versions: look.versions.map(version => {
      const outputId =
        toCollectionBuildOutputId(version.outputId) ??
        toCollectionBuildOutputId(version.id);
      if (!outputId) {
        return version;
      }
      const approvedAt =
        version.approvedAt || approvedAtByOutputId.get(outputId) || null;
      return {
        ...version,
        outputId,
        approvedAt,
      };
    }),
  }));
};

export const collectionLooksFromOutputs = (
  outputs: CollectionBuildHistoryRecord[]
): CollectionBuildLook[] => {
  const looks = outputs.filter(item => item.outputType === "look");
  const groups = new Map<number, CollectionBuildHistoryRecord[]>();
  looks.forEach(item => {
    const key = item.outputNumber || item.id;
    const list = groups.get(key) || [];
    list.push(item);
    groups.set(key, list);
  });
  return Array.from(groups.entries())
    .sort(([left], [right]) => left - right)
    .map(([number, items]) => {
      const sorted = [...items].sort(
        (left, right) =>
          new Date(right.createdAt).getTime() -
          new Date(left.createdAt).getTime()
      );
      const current = sorted[0];
      const payload = (current.requestPayload || {}) as {
        look?: { styleIndex?: number; productType?: string };
        styleIndex?: number;
      };
      return {
        id: `look-${number}`,
        number,
        productType:
          current.details?.productType || payload.look?.productType || "Look",
        styleIndex:
          payload.look?.styleIndex ?? payload.styleIndex ?? number - 1,
        versions: sorted.map(item => ({
          id: String(item.id),
          outputId: toCollectionBuildOutputId(item.id) ?? undefined,
          status: item.status,
          frontUrl: item.imageUrl || "",
          backUrl: item.backImageUrl || "",
          approvedAt: item.review?.approvedAt || null,
        })),
      };
    });
};

export const collectionMoodboardsFromOutputs = (
  outputs: CollectionBuildHistoryRecord[]
): CollectionBuildMoodboardVersion[] =>
  outputs
    .filter(item => item.outputType === "moodboard")
    .sort(
      (left, right) =>
        new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
    )
    .map(item => ({
      id: String(item.id),
      outputId: item.id,
      imageUrl: item.moodboardUrl || item.imageUrl || "",
      svgUrl: item.details?.svgUrl || null,
      approvedAt: item.review?.approvedAt || null,
    }));

/** Approved viewed moodboard history id for POST generate/looks (omit if invalid). */
export const moodboardGenerationIdForLooks = (
  outputId: number | undefined,
  approvedAt: string | null | undefined
): number | undefined => {
  if (!approvedAt || outputId == null) {
    return undefined;
  }
  const id = Number(outputId);
  if (!Number.isInteger(id) || id < 1) {
    return undefined;
  }
  return id;
};

const colourStoryIdsFromInput = (
  input: Partial<CollectionBuildSessionPayload>
) => {
  if (Array.isArray(input.colourStoryIds) && input.colourStoryIds.length) {
    return input.colourStoryIds
      .map(id => String(id || "").trim())
      .filter(Boolean);
  }
  return input.colourStoryId ? [input.colourStoryId] : [];
};

/** Prefer surfaceIds[]; fall back to legacy single surfaceId. */
const surfaceIdsFromInput = (input: Partial<CollectionBuildSessionPayload>) => {
  if (Array.isArray(input.surfaceIds) && input.surfaceIds.length) {
    const seen = new Set<string>();
    const next: string[] = [];
    input.surfaceIds.forEach(id => {
      const trimmed = String(id || "").trim();
      if (!trimmed || seen.has(trimmed)) {
        return;
      }
      seen.add(trimmed);
      next.push(trimmed);
    });
    return next;
  }
  const single = String(input.surfaceId || "").trim();
  return single ? [single] : [];
};

type PaletteSource = {
  palettes?: ColorPaletteOption[];
  paletteHexes?: string[];
  colourStoryId?: string;
};

/** Keep each colour story as its own group — never concatenate hexes first. */
const groupedPalettesFrom = (
  palettes?: ColorPaletteOption[] | null
): ColorPaletteOption[] => {
  if (!Array.isArray(palettes) || !palettes.length) {
    return [];
  }
  return palettes
    .filter(item => item && Array.isArray(item.colors) && item.colors.length)
    .map(item => ({
      id: item.id,
      label: item.label,
      colors: [...item.colors],
      swatches: item.swatches?.map(swatch => ({ ...swatch })),
    }));
};

const palettesFromHexes = (
  hexes: string[] | undefined,
  colourStoryId?: string
): ColorPaletteOption[] => {
  if (!hexes?.length) {
    return [];
  }
  return [
    {
      id: colourStoryId || "palette",
      label: "Color Palette",
      colors: [...hexes],
    },
  ];
};

const palettesFromInput = (input: PaletteSource): ColorPaletteOption[] => {
  const grouped = groupedPalettesFrom(input.palettes);
  if (grouped.length) {
    return grouped;
  }
  return palettesFromHexes(input.paletteHexes, input.colourStoryId);
};

/** Prefer per-palette groups from any source; flatten hexes only as last resort. */
const palettesFromSessionSources = (
  ...sources: PaletteSource[]
): ColorPaletteOption[] => {
  for (const source of sources) {
    const grouped = groupedPalettesFrom(source.palettes);
    if (grouped.length) {
      return grouped;
    }
  }
  for (const source of sources) {
    const fromHex = palettesFromHexes(
      source.paletteHexes,
      source.colourStoryId
    );
    if (fromHex.length) {
      return fromHex;
    }
  }
  return [];
};

/** Map API startingDirection slugs to query-form pill labels. */
export const startingDirectionLabelFromValue = (
  value: unknown
): StartingDirection | "" => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");
  if (normalized === "denim") {
    return "Denim";
  }
  if (normalized === "capsules" || normalized === "capsule") {
    return "Capsules";
  }
  if (
    normalized === "macro-theme" ||
    normalized === "macrotheme" ||
    normalized === "macro"
  ) {
    return "Macro-theme";
  }
  return "";
};

/** Prefer `patternTrendIds`; fall back to singular id / legacy microTheme. */
const patternTrendIdsFromInput = (
  input: Partial<CollectionBuildSessionPayload> & { microTheme?: string }
): string[] => {
  const fromArray = Array.isArray(input.patternTrendIds)
    ? input.patternTrendIds.map(id => String(id || "").trim()).filter(Boolean)
    : [];
  if (fromArray.length) {
    const seen = new Set<string>();
    return fromArray.filter(id => {
      if (seen.has(id)) {
        return false;
      }
      seen.add(id);
      return true;
    });
  }
  const single = String(input.patternTrendId || input.microTheme || "").trim();
  return single ? [single] : [];
};

export type CollectionBuildFormHydration = {
  market: string;
  category: string;
  seasonId: string;
  seasonLabel: string;
  rangePlan: Array<{ productType: string; quantity: number }>;
  startingDirection: StartingDirection | "";
  macroTheme: string;
  selectedPaletteIds: string[];
  extraPalettes: ColorPaletteOption[];
  denimMaterialId: string;
  denimWashId: string;
  patternChoice: PatternTrendChoice | "";
  /** patternTrendIds from session: active micro-theme first, then other selected themes. */
  microThemes: string[];
  /** Surface-report slide ids (multi-select); empty when pattern path. */
  surfaceIds: string[];
  selectedPreferenceIds: string[];
  garmentFormat: string;
  embroideryType: string;
  patternFormat: string;
  additionalPrompt: string;
  skipMoodboard: boolean;
  /** Persisted Look Details draft for silhouette reopen/hydrate. */
  looks: CollectionBuildLookPayload[];
};

/**
 * Extract query-form values from a fetched CB session (draft or generated).
 * Prefer `inputSelections`; fall back to top-level `rangePlan` / `skipMoodboard`.
 */
export const formHydrationFromCollectionSession = (
  session: CollectionBuildSessionDetail
): CollectionBuildFormHydration | null => {
  const input = (session.inputSelections ||
    {}) as Partial<CollectionBuildSessionPayload> & {
    macroTheme?: string;
    microTheme?: string;
  };
  const rangePlan = (
    session.rangePlan?.items ||
    input.rangePlan?.items ||
    []
  ).filter(
    item =>
      item &&
      typeof item.productType === "string" &&
      item.productType.trim() &&
      Number(item.quantity) > 0
  );
  const market = String(input.market || "").trim();
  const category = String(input.category || "").trim();
  const seasonId = String(input.seasonId || "").trim();
  const seasonLabel = String(input.season || "").trim();
  if (!market && !category && !seasonId && !seasonLabel && !rangePlan.length) {
    return null;
  }
  const colourStoryIds = colourStoryIdsFromInput(input);
  const palettes = palettesFromInput(input);
  const catalogPaletteIds = new Set(colourStoryIds);
  const extraPalettes = palettes.filter(
    palette => palette.id && !catalogPaletteIds.has(palette.id)
  );
  const startingDirection = startingDirectionLabelFromValue(
    input.startingDirection
  );
  const isDenim = startingDirection === "Denim";
  const patternChoiceRaw = String(input.patternChoice || "").trim();
  const surfaceIds = isDenim ? [] : surfaceIdsFromInput(input);
  const patternChoice: PatternTrendChoice | "" = isDenim
    ? ""
    : surfaceIds.length
      ? ""
      : patternChoiceRaw === "choose" || patternChoiceRaw === "recommend"
        ? patternChoiceRaw
        : "";
  const microThemes =
    isDenim || surfaceIds.length ? [] : patternTrendIdsFromInput(input);

  return {
    market,
    category,
    seasonId,
    seasonLabel,
    rangePlan: rangePlan.map(item => ({
      productType: item.productType.trim(),
      quantity: Math.max(0, Number(item.quantity) || 0),
    })),
    startingDirection,
    macroTheme: isDenim
      ? ""
      : String(input.macroThemeId || input.macroTheme || "").trim(),
    selectedPaletteIds: colourStoryIds,
    extraPalettes,
    denimMaterialId: String(input.denimMaterialId || "").trim(),
    denimWashId: String(input.denimWashId || "").trim(),
    patternChoice,
    microThemes,
    surfaceIds,
    selectedPreferenceIds:
      isDenim || surfaceIds.length
        ? []
        : (input.selectedAssetStems || [])
            .map(stem => String(stem || "").trim())
            .filter(Boolean),
    garmentFormat: isDenim ? "" : String(input.garmentFormat || "").trim(),
    embroideryType: isDenim ? "" : String(input.embroideryType || "").trim(),
    patternFormat: isDenim ? "" : String(input.patternFormat || "").trim(),
    additionalPrompt: String(input.additionalPrompt || "").trim(),
    skipMoodboard: Boolean(session.skipMoodboard || input.skipMoodboard),
    looks: Array.isArray(input.looks)
      ? input.looks.map(look => {
          const sanitized = sanitizeCollectionBuildLookPayload(
            look as CollectionBuildLookPayload
          );
          // Legacy sessions stored Print/Embroidery once at session level.
          if (
            isDenim ||
            sanitized.garmentFormat ||
            (sanitized.garmentFormats && sanitized.garmentFormats.length > 0)
          ) {
            return sanitized;
          }
          const sessionFormat = String(input.garmentFormat || "").trim();
          if (!sessionFormat) {
            return sanitized;
          }
          return sanitizeCollectionBuildLookPayload({
            ...sanitized,
            garmentFormat: sessionFormat,
            patternFormat:
              sanitized.patternFormat ||
              String(input.patternFormat || "").trim() ||
              undefined,
            embroideryType:
              sessionFormat === "Embroidery"
                ? sanitized.embroideryType ||
                  String(input.embroideryType || "").trim() ||
                  undefined
                : undefined,
          });
        })
      : [],
  };
};

const uniqueAssetStems = (values: unknown[]): string[] => {
  const seen = new Set<string>();
  const stems: string[] = [];
  values.forEach(value => {
    const stem = String(value || "").trim();
    if (!stem || seen.has(stem)) {
      return;
    }
    seen.add(stem);
    stems.push(stem);
  });
  return stems;
};

const stemsFromPayload = (payload: unknown): string[] => {
  if (!payload || typeof payload !== "object") {
    return [];
  }
  const stems = (payload as { selectedAssetStems?: unknown })
    .selectedAssetStems;
  if (!Array.isArray(stems)) {
    return [];
  }
  return uniqueAssetStems(stems);
};

const selectedAssetStemsFromSession = (
  input:
    | Partial<CollectionBuildSessionPayload>
    | Record<string, unknown>
    | undefined,
  outputs: CollectionBuildHistoryRecord[]
): string[] => {
  const inputStems = stemsFromPayload(input);
  if (inputStems.length) {
    return inputStems;
  }
  return uniqueAssetStems([
    ...outputs.flatMap(item => stemsFromPayload(item.requestPayload)),
    ...outputs.flatMap(item =>
      (item.details?.preferences || []).map(pref => pref.stem)
    ),
    ...outputs.flatMap(item =>
      (item.references?.assets || [])
        .filter(asset => asset.attached !== false)
        .map(asset => asset.stem)
    ),
  ]);
};

const persistPayloadFromSession = (
  session: CollectionBuildSessionDetail,
  input: Partial<CollectionBuildSessionPayload> & {
    macroTheme?: string;
    microTheme?: string;
  },
  rangePlan: CollectionBuildSessionPayload["rangePlan"]["items"]
): CollectionBuildSessionPayload => {
  const colourStoryIds = colourStoryIdsFromInput(input);
  const palettes = palettesFromSessionSources(input, {
    palettes: session.outputs[0]?.details?.palettes,
    paletteHexes: session.outputs[0]?.details?.paletteHexes,
    colourStoryId: colourStoryIds[0],
  });
  const market = input.market || session.outputs[0]?.details?.market || "";
  const category =
    input.category || session.outputs[0]?.details?.category || "";
  const season = input.season || session.outputs[0]?.details?.season || "";
  const themeLabel =
    collectionThemeLabelFromSession(session) ||
    String(input.themeLabel || "").trim() ||
    undefined;
  const sessionTitle = themeLabel
    ? collectionBuildSummaryTitle({
        market,
        category,
        season,
        theme: themeLabel,
      })
    : String(input.sessionTitle || "").trim() || undefined;
  const surfaceIds = surfaceIdsFromInput(input);
  return sanitizeCollectionBuildSessionPayload({
    season,
    seasonId: input.seasonId,
    genre: input.genre || session.outputs[0]?.details?.genre || "Fashion",
    market,
    category,
    startingDirection: input.startingDirection,
    skipReportThemes: input.skipReportThemes,
    skipMoodboard: Boolean(session.skipMoodboard || input.skipMoodboard),
    macroThemeId: input.macroThemeId || input.macroTheme,
    colourStoryId: colourStoryIds[0],
    colourStoryIds,
    patternTrendId:
      patternTrendIdsFromInput(input)[0] ||
      input.patternTrendId ||
      input.microTheme,
    patternTrendIds: patternTrendIdsFromInput(input),
    surfaceId: surfaceIds[0],
    surfaceIds,
    patternChoice: input.patternChoice,
    selectedAssetStems: selectedAssetStemsFromSession(input, session.outputs),
    palettes,
    paletteHexes:
      input.paletteHexes ||
      palettes.flatMap(item => item.colors) ||
      session.outputs[0]?.details?.paletteHexes ||
      [],
    garmentFormat: input.garmentFormat,
    embroideryType: input.embroideryType,
    patternFormat: input.patternFormat,
    denimMaterialId: input.denimMaterialId,
    denimWashId: input.denimWashId,
    additionalPrompt: input.additionalPrompt,
    themeLabel,
    sessionTitle,
    rangePlan: { items: rangePlan },
    looks: Array.isArray(input.looks)
      ? input.looks.map(look =>
          sanitizeCollectionBuildLookPayload(look as CollectionBuildLookPayload)
        )
      : undefined,
  });
};

const patternChoiceDisplay = (value: unknown): string => {
  const raw = String(value || "").trim();
  if (raw === "choose" || raw === "recommend") {
    return patternTrendLabel(raw);
  }
  return "";
};

export const preferencesFromCollectionSession = (
  input:
    | Partial<CollectionBuildSessionPayload>
    | Record<string, unknown>
    | undefined,
  outputs: CollectionBuildHistoryRecord[]
): PreferenceOption[] => {
  const records = outputs || [];
  const outputPrefs = records
    .flatMap(item => item.details?.preferences || [])
    .filter(item => item?.stem);
  const byStem = new Map<string, string>();
  outputPrefs.forEach(item => {
    const url = item.url || (item as { imageUrl?: string }).imageUrl || "";
    if (url && !byStem.has(item.stem)) {
      byStem.set(item.stem, url);
    }
  });
  const stems = selectedAssetStemsFromSession(input, records);
  if (stems.length) {
    return stems.map(stem => ({
      id: stem,
      imageUrl: byStem.get(stem) || "",
      label: stem,
    }));
  }
  const seen = new Set<string>();
  return outputPrefs
    .filter(item => {
      if (seen.has(item.stem)) {
        return false;
      }
      seen.add(item.stem);
      return true;
    })
    .map(item => ({
      id: item.stem,
      imageUrl: item.url || (item as { imageUrl?: string }).imageUrl || "",
      label: item.stem,
    }));
};

export const preferencesFromCollectionRecords = (
  records: CollectionBuildHistoryRecord[]
): PreferenceOption[] =>
  preferencesFromCollectionSession(
    records[0]?.requestPayload as
      | Partial<CollectionBuildSessionPayload>
      | undefined,
    records
  );

/** Fill Recommend-path prefs from assemble/poll without replacing Choose selections. */
export const mergeCollectionBuildDetailsPreferences = (
  current: CollectionBuildResultDetails,
  incoming: PreferenceOption[]
): CollectionBuildResultDetails => {
  // Denim Design Details uses material/wash catalog tiles, not pattern-trend prefs.
  if (/denim/i.test(current.startingDirection || "")) {
    return current;
  }
  const next = incoming
    .map(item => ({
      id: String(item.id || "").trim(),
      imageUrl: String(item.imageUrl || "").trim(),
      label: String(item.label || item.id || "").trim(),
    }))
    .filter(item => item.id);
  if (!next.length) {
    return current;
  }

  const persist = {
    ...((current.persistPayload || {}) as CollectionBuildSessionPayload &
      Record<string, unknown>),
  };
  const persistStems = uniqueAssetStems(
    Array.isArray(persist.selectedAssetStems) ? persist.selectedAssetStems : []
  );
  const currentPreferences = current.preferences || [];
  const patternChoice = String(persist.patternChoice || "").trim();
  const replace =
    currentPreferences.length === 0 ||
    (patternChoice === "recommend" && persistStems.length === 0);

  const preferences = replace
    ? next
    : currentPreferences.map(item => {
        const match = next.find(pref => pref.id === item.id);
        if (!match) {
          return item;
        }
        if (item.imageUrl && !item.imageUrl.startsWith("blob:")) {
          return item.label ? item : { ...item, label: match.label || item.id };
        }
        return {
          ...item,
          imageUrl: match.imageUrl || item.imageUrl,
          label: item.label || match.label || item.id,
        };
      });

  const nextStems = replace
    ? next.map(item => item.id)
    : persistStems.length
      ? persistStems
      : currentPreferences.map(item => item.id).filter(Boolean);

  const samePrefs =
    preferences.length === currentPreferences.length &&
    preferences.every(
      (item, index) =>
        item.id === currentPreferences[index]?.id &&
        item.imageUrl === currentPreferences[index]?.imageUrl &&
        item.label === currentPreferences[index]?.label
    );
  if (samePrefs) {
    return current;
  }

  return snapshotCollectionBuildDetails({
    ...current,
    preferences,
    persistPayload: {
      ...persist,
      selectedAssetStems: nextStems,
    },
  });
};

const macroThemeDisplayFromSession = (
  input: Partial<CollectionBuildSessionPayload> & { macroTheme?: string },
  outputs: CollectionBuildSessionDetail["outputs"]
): string => {
  const rawId = String(input.macroThemeId || input.macroTheme || "").trim();
  const fromDetails = String(outputs[0]?.details?.macroTheme || "").trim();
  const fromRef = String(outputs[0]?.references?.macroTheme || "").trim();
  // Prefer a non-id display string from output details/refs when present.
  if (fromDetails && !fromDetails.includes("|") && fromDetails !== rawId) {
    return fromDetails;
  }
  if (fromRef && !fromRef.includes("|") && fromRef !== rawId) {
    return fromRef;
  }
  // Keep the catalog id so enrichCollectionBuildDetails can resolve the real name.
  return rawId || fromDetails || fromRef;
};

const microThemeDisplayFromSession = (
  input: Partial<CollectionBuildSessionPayload> & { microTheme?: string },
  outputs: CollectionBuildSessionDetail["outputs"]
): string => {
  const ids = patternTrendIdsFromInput(input);
  const fromDetails = String(outputs[0]?.details?.microTheme || "").trim();
  const fromRef = String(outputs[0]?.references?.patternTrend || "").trim();
  // Prefer all selected patternTrendIds so Design Details never drops to the
  // first theme alone; enrich resolves ids → catalog labels.
  if (ids.length > 1) {
    return ids.join(", ");
  }
  if (ids.length === 1) {
    if (
      fromDetails &&
      fromDetails !== ids[0] &&
      !ids.includes(fromDetails) &&
      !/^\d+$/.test(fromDetails)
    ) {
      return fromDetails;
    }
    if (
      fromRef &&
      fromRef !== ids[0] &&
      !ids.includes(fromRef) &&
      !/^\d+$/.test(fromRef)
    ) {
      return fromRef;
    }
    return ids[0];
  }
  // Prefer a human display string already frozen on outputs (may be multi joined).
  if (fromDetails && !/^\d+$/.test(fromDetails)) {
    return fromDetails;
  }
  if (fromRef && !/^\d+$/.test(fromRef)) {
    return fromRef;
  }
  return fromDetails || fromRef;
};

const denimTilesFromIds = (
  isDenim: boolean,
  materialId: string,
  washId: string,
  looks?: Array<{
    denimMaterialId?: string | null;
    denimWashId?: string | null;
  }> | null
) => {
  if (!isDenim) {
    return {
      denimMaterialTile: undefined,
      denimWashTile: undefined,
      denimMaterialTiles: undefined,
      denimWashTiles: undefined,
    };
  }
  const fromLooks = denimTilesFromStyleSelections(looks || []);
  if (fromLooks.denimMaterialTiles.length || fromLooks.denimWashTiles.length) {
    return {
      denimMaterialTile:
        fromLooks.denimMaterialTile ||
        resolveDenimCatalogTile("materials", materialId) ||
        undefined,
      denimWashTile:
        fromLooks.denimWashTile ||
        resolveDenimCatalogTile("washes", washId) ||
        undefined,
      denimMaterialTiles: fromLooks.denimMaterialTiles.length
        ? fromLooks.denimMaterialTiles
        : undefined,
      denimWashTiles: fromLooks.denimWashTiles.length
        ? fromLooks.denimWashTiles
        : undefined,
    };
  }
  const materialTile =
    resolveDenimCatalogTile("materials", materialId) || undefined;
  const washTile = resolveDenimCatalogTile("washes", washId) || undefined;
  return {
    denimMaterialTile: materialTile,
    denimWashTile: washTile,
    denimMaterialTiles: materialTile ? [materialTile] : undefined,
    denimWashTiles: washTile ? [washTile] : undefined,
  };
};

/** First surface slide keeps the name stored on the generated output. */
const surfaceTrendsFromSession = (
  input: Partial<CollectionBuildSessionPayload>,
  outputs: CollectionBuildSessionDetail["outputs"]
) => {
  const ids = surfaceIdsFromInput(input);
  const surfaceId = ids[0];
  if (!surfaceId) {
    return undefined;
  }
  const name = (outputs || [])
    .map(item =>
      String(
        item.details?.microTheme || item.references?.patternTrend || ""
      ).trim()
    )
    .find(
      value =>
        Boolean(value) &&
        value !== surfaceId &&
        !/^\d+$/.test(value) &&
        !ids.includes(value)
    );
  return name ? [{ id: surfaceId, name }] : undefined;
};

export const detailsFromCollectionSession = (
  session: CollectionBuildSessionDetail
): CollectionBuildResultDetails | null => {
  const input = (session.inputSelections ||
    {}) as Partial<CollectionBuildSessionPayload> & {
    macroTheme?: string;
    microTheme?: string;
    patternTrend?: string;
  };
  const rangePlan = session.rangePlan?.items || input.rangePlan?.items || [];
  const outputDetails = session.outputs[0]?.details;
  const palettes = palettesFromSessionSources(input, {
    palettes: outputDetails?.palettes,
    paletteHexes: outputDetails?.paletteHexes,
    colourStoryId: colourStoryIdsFromInput(input)[0],
  });
  const paletteHexes = palettes.flatMap(item => item.colors);
  const denimFromInput =
    startingDirectionLabelFromValue(input.startingDirection) === "Denim";
  if (!input.season && !input.market) {
    const first = session.outputs[0];
    if (!first?.details) {
      return null;
    }
    const startingDirection =
      startingDirectionLabelFromValue(first.details.startingDirection) ||
      first.details.startingDirection ||
      "";
    const isDenim =
      denimFromInput ||
      startingDirectionLabelFromValue(startingDirection) === "Denim";
    const microThemeJoined = isDenim
      ? ""
      : microThemeDisplayFromSession(input, session.outputs);
    const microThemes = microThemeJoined
      ? microThemeJoined
          .split(",")
          .map(part => part.trim())
          .filter(Boolean)
      : [];
    return snapshotCollectionBuildDetails({
      genre: first.details.genre,
      season: first.details.season,
      market: first.details.market,
      category: first.details.category,
      rangePlanTotal:
        first.details.rangePlanTotal ||
        rangePlan.reduce((sum, item) => sum + item.quantity, 0),
      rangePlanBreakdown: first.details.rangePlanBreakdown || rangePlan,
      startingDirection,
      macroTheme: isDenim ? "" : first.details.macroTheme || "",
      patternTrend: isDenim
        ? ""
        : patternChoiceDisplay(first.details.patternChoice),
      microTheme: microThemeJoined,
      microThemes,
      wash: resolveDenimWashDisplay(
        String(input.denimWashId || first.details.denimWashId || "").trim()
      ),
      ...denimTilesFromIds(
        isDenim,
        String(
          input.denimMaterialId || first.details.denimMaterialId || ""
        ).trim(),
        String(input.denimWashId || first.details.denimWashId || "").trim(),
        input.looks
      ),
      garmentFormat: isDenim ? "" : first.details.garmentFormat || "",
      patternFormat: isDenim ? "" : first.details.patternFormat || "",
      palettes,
      paletteHexes,
      preferences: isDenim
        ? []
        : preferencesFromCollectionSession(input, session.outputs),
      surfaceTrends: isDenim
        ? undefined
        : surfaceTrendsFromSession(input, session.outputs),
      skipMoodboard: Boolean(
        session.skipMoodboard || first.details.skipMoodboard
      ),
      persistPayload: persistPayloadFromSession(session, input, rangePlan),
    });
  }
  const startingDirectionLabel =
    startingDirectionLabelFromValue(input.startingDirection) ||
    String(input.startingDirection || "").trim();
  const isDenim =
    denimFromInput ||
    startingDirectionLabelFromValue(startingDirectionLabel) === "Denim";
  const microThemeJoined = isDenim
    ? ""
    : microThemeDisplayFromSession(input, session.outputs);
  const microThemes = microThemeJoined
    ? microThemeJoined
        .split(",")
        .map(part => part.trim())
        .filter(Boolean)
    : [];

  return snapshotCollectionBuildDetails({
    genre: input.genre || "Fashion",
    season: input.season || "",
    market: input.market || "",
    category: input.category || "",
    rangePlanTotal: rangePlan.reduce((sum, item) => sum + item.quantity, 0),
    rangePlanBreakdown: rangePlan,
    startingDirection: startingDirectionLabel,
    macroTheme: isDenim
      ? ""
      : macroThemeDisplayFromSession(input, session.outputs),
    patternTrend: isDenim
      ? ""
      : patternChoiceDisplay(
          input.patternChoice || session.outputs[0]?.details?.patternChoice
        ),
    microTheme: microThemeJoined,
    microThemes,
    wash: resolveDenimWashDisplay(String(input.denimWashId || "").trim()),
    ...denimTilesFromIds(
      isDenim,
      String(input.denimMaterialId || "").trim(),
      String(input.denimWashId || "").trim(),
      input.looks
    ),
    garmentFormat: isDenim ? "" : input.garmentFormat || "",
    patternFormat: isDenim ? "" : input.patternFormat || "",
    palettes,
    paletteHexes,
    preferences: isDenim
      ? []
      : preferencesFromCollectionSession(input, session.outputs),
    surfaceTrends: isDenim
      ? undefined
      : surfaceTrendsFromSession(input, session.outputs),
    skipMoodboard: Boolean(session.skipMoodboard || input.skipMoodboard),
    persistPayload: persistPayloadFromSession(session, input, rangePlan),
  });
};
